// Auto-extracted tab: documents
import React, { useState, useEffect, useMemo, useCallback } from 'react';

/**
 * DocumentsTab - extracted from app.jsx
 * Original lines: 7806-8159
 * 
 * TODO: This component needs props passed from AppContent:
 * - darkMode, scenarios, thresholds, etc.
 * - All the state and handlers it uses
 */
export function DocumentsTab(props) {
  // Original render content (needs refactoring to use props):
  return (
    <>
          <div className="space-y-6">
            <Card
              title="Evidence Library"
              subtitle="Controlled archive with SHA-256 integrity verification"
              actions={
                <div className="flex items-center gap-2">
                  <select
                    aria-label="Select document type for upload"
                    className="border border-slate-200 rounded-xl px-4 py-2.5 text-sm bg-white transition-all duration-200 hover:border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:ring-offset-0"
                    value={documentType}
                    onChange={e => setDocumentType(e.target.value)}
                  >
                    <option value="" disabled>Document Type</option>
                    <option value="policy">Policy</option>
                    <option value="procedure">Procedure</option>
                    <option value="evidence">Evidence</option>
                    <option value="board_minutes">Board Minutes</option>
                    <option value="risk_assessment">Risk Assessment</option>
                    <option value="regulatory">Regulatory</option>
                    <option value="exam">Exam</option>
                    <option value="correspondence">Correspondence</option>
                    <option value="other">Other</option>
                  </select>
                  <input
                    id="document-file-input"
                    aria-label="Upload document file"
                    key={documentInputKey}
                    type="file"
                    accept=".pdf,.docx,.txt,.md"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      if (!orgId) {
                        setToast({ type: "error", message: "Organization not loaded. Please refresh." });
                        setTimeout(() => setToast(null), 5000);
                        return;
                      }
                      setDocumentFile(file);
                      setUploadingDocument(true);
                      try {
                        const form = new FormData();
                        form.append("file", file);
                        form.append("organization_id", orgId);
                        form.append("vertical", vertical);
                        form.append("document_type", documentType || "evidence");
                        await callFn("upload-document", form, true);
                        setToast({ type: "success", message: `Uploaded: ${file.name}` });
                        setTimeout(() => setToast(null), 3000);
                        loadAllData();
                        setDocumentFile(null);
                        setDocumentInputKey(k => k + 1);
                      } catch (err) {
                        console.error("Upload error:", err);
                        setToast({ type: "error", message: sanitizeErrorMessage(err) || "Upload failed" });
                        setTimeout(() => setToast(null), 5000);
                      } finally {
                        setUploadingDocument(false);
                      }
                    }}
                  />
                  <Button
                    variant="primary"
                    disabled={uploadingDocument || !orgId}
                    onClick={() => document.getElementById("document-file-input")?.click()}
                  >
                    <Icons.Upload /> {uploadingDocument ? "Uploading..." : "Upload"}
                  </Button>
                </div>
              }
            >
              {!org?.ai_scanning_enabled && (
                <div className="mb-4 bg-amber-50 border border-amber-100 rounded-lg p-3 text-sm text-amber-800 flex items-center gap-2">
                  <Icons.Alert className="flex-shrink-0" />
                  AI scanning is disabled for this organization. Documents can be uploaded but not scanned.
                </div>
              )}

              <div className="grid lg:grid-cols-12 gap-4">
                {/* Left Filter Panel */}
                <div className="lg:col-span-3 space-y-4">
                  <div className="bg-white border border-slate-200 rounded-lg p-4">
                    <div className="text-sm font-semibold text-slate-900 mb-3">Filters</div>
                    <div className="space-y-3">
                      <div>
                        <label className="text-xs font-medium text-slate-600">Type</label>
                        <select
                          className="mt-1 w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm bg-white"
                          value={docFilters.type}
                          onChange={e => setDocFilters(f => ({ ...f, type: e.target.value }))}
                        >
                          <option value="all">All Types</option>
                          <option value="policy">Policy</option>
                          <option value="procedure">Procedure</option>
                          <option value="evidence">Evidence</option>
                          <option value="board_minutes">Board Minutes</option>
                          <option value="risk_assessment">Risk Assessment</option>
                          <option value="regulatory">Regulatory</option>
                          <option value="exam">Exam</option>
                          <option value="correspondence">Correspondence</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-slate-600">Integrity</label>
                        <select
                          className="mt-1 w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm bg-white"
                          value={docFilters.integrity}
                          onChange={e => setDocFilters(f => ({ ...f, integrity: e.target.value }))}
                        >
                          <option value="all">All</option>
                          <option value="verified">Verified</option>
                          <option value="modified">Modified</option>
                          <option value="pending">Pending</option>
                          <option value="error">Error</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-slate-600">Status</label>
                        <select
                          className="mt-1 w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm bg-white"
                          value={docFilters.status}
                          onChange={e => setDocFilters(f => ({ ...f, status: e.target.value }))}
                        >
                          <option value="all">All</option>
                          <option value="uploaded">Uploaded</option>
                          <option value="scanned">Scanned</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-slate-600">Linked Question</label>
                        <select
                          className="mt-1 w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm bg-white"
                          value={docFilters.question}
                          onChange={e => setDocFilters(f => ({ ...f, question: e.target.value }))}
                        >
                          <option value="all">All</option>
                          <option value="WHO">WHO</option>
                          <option value="WHAT">WHAT</option>
                          <option value="WHEN">WHEN</option>
                          <option value="WHY">WHY</option>
                          <option value="WHERE">WHERE</option>
                          <option value="AUTHORITY">AUTHORITY</option>
                          <option value="EVIDENCE">EVIDENCE</option>
                          <option value="REVIEW">REVIEW</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Main Table */}
                <div className="lg:col-span-6">
                  <div className="rounded-xl overflow-hidden border border-slate-200/50 shadow-sm">
                    <table className="w-full text-sm">
                      <thead className="bg-gradient-to-r from-slate-50 to-slate-100/80">
                        <tr>
                          <th className="px-4 py-3.5 text-left font-semibold text-slate-700">Title</th>
                          <th className="px-3 py-2 text-left font-medium text-slate-600">Type</th>
                          <th className="px-3 py-2 text-left font-medium text-slate-600">Integrity</th>
                          <th className="px-3 py-2 text-left font-medium text-slate-600">Linked</th>
                          <th className="px-3 py-2 text-left font-medium text-slate-600">Uploaded</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {dataLoading ? (
                          <tr><td colSpan={5} className="px-4 py-16">
                            <div className="flex flex-col items-center justify-center">
                              <Icons.Refresh className="w-8 h-8 text-emerald-500 animate-spin mb-3" />
                              <div className="text-slate-600 font-medium">Loading documents...</div>
                            </div>
                          </td></tr>
                        ) : filteredDocuments.length === 0 ? (
                          <tr><td colSpan={5} className="px-4 py-16">
                            <div className="flex flex-col items-center justify-center text-center">
                              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-100 to-teal-100 flex items-center justify-center mb-4">
                                <Icons.Briefcase className="w-8 h-8 text-emerald-500" />
                              </div>
                              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                                {documents.length === 0 ? "No evidence uploaded" : "No documents match filters"}
                              </h3>
                              <p className="text-slate-500 mb-6 max-w-sm">
                                {documents.length === 0 
                                  ? "Upload your first evidence document to build your compliance audit trail."
                                  : "Try adjusting your filter criteria to see more documents."}
                              </p>
                              {documents.length === 0 && (
                                <Button variant="primary" onClick={() => document.getElementById('document-file-input')?.click()}>
                                  <Icons.Upload className="w-4 h-4 mr-2" /> Upload Document
                                </Button>
                              )}
                            </div>
                          </td></tr>
                        ) : filteredDocuments.map(doc => {
                          const links = documentLinks.filter(l => l.document_id === doc.id);
                          const integrityColor = doc.integrity_status === "verified"
                            ? "emerald"
                            : doc.integrity_status === "modified"
                              ? "red"
                              : doc.integrity_status === "pending"
                                ? "amber"
                                : "red";
                          return (
                            <tr
                              key={doc.id}
                              className={`hover:bg-slate-50 cursor-pointer ${selectedDocument?.id === doc.id ? "bg-slate-50" : ""}`}
                              onClick={() => setSelectedDocument(doc)}
                            >
                              <td className="px-3 py-2">
                                <div className="font-medium text-slate-900">{doc.filename}</div>
                                <div className="text-xs text-slate-500">{doc.mime_type}</div>
                              </td>
                              <td className="px-3 py-2"><Badge color="blue">{doc.document_type || "other"}</Badge></td>
                              <td className="px-3 py-2">
                                <Badge color={integrityColor}>
                                  {doc.integrity_status === "verified"
                                    ? "Verified"
                                    : doc.integrity_status === "modified"
                                      ? "Modified"
                                      : doc.integrity_status || "pending"}
                                </Badge>
                              </td>
                              <td className="px-3 py-2 text-slate-500 text-xs">
                                {links.length > 0 ? `${links.length} link${links.length !== 1 ? "s" : ""}` : "—"}
                              </td>
                              <td className="px-3 py-2 text-slate-500">{formatDate(doc.created_at)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Right Preview Panel */}
                <div className="lg:col-span-3">
                  <div className="bg-white border border-slate-200 rounded-lg p-4 sticky top-24">
                    <div className="text-sm font-semibold text-slate-900 mb-3">Preview</div>
                    {!selectedDocument ? (
                      <div className="text-sm text-slate-500">Select a document to view details.</div>
                    ) : (
                      <div className="space-y-4">
                        <div>
                          <div className="font-medium text-slate-900">{selectedDocument.filename}</div>
                          <div className="text-xs text-slate-500">{selectedDocument.mime_type}</div>
                        </div>
                        <div className="space-y-2 text-sm">
                          <div><span className="text-slate-500">Type:</span> {selectedDocument.document_type || "other"}</div>
                          <div><span className="text-slate-500">Size:</span> {Math.round((selectedDocument.size_bytes || 0) / 1024)} KB</div>
                          <div><span className="text-slate-500">Uploaded:</span> {formatDateTime(selectedDocument.created_at)}</div>
                          <div>
                            <span className="text-slate-500">Integrity:</span>{" "}
                            <Badge color={selectedDocument.integrity_status === "verified" ? "emerald" : selectedDocument.integrity_status === "modified" ? "red" : "amber"}>
                              {selectedDocument.integrity_status || "pending"}
                            </Badge>
                          </div>
                          <div className="text-xs text-slate-500 break-all">
                            <div className="font-medium text-slate-600">Content Hash (SHA-256)</div>
                            {selectedDocument.content_hash || "Not computed"}
                          </div>
                        </div>

                        {/* Linked objects */}
                        <div>
                          <div className="text-xs font-medium text-slate-600 mb-1">Linked Objects</div>
                          <div className="flex flex-wrap gap-1">
                            {documentLinks.filter(l => l.document_id === selectedDocument.id).length === 0 ? (
                              <span className="text-xs text-slate-500">No links</span>
                            ) : (
                              documentLinks.filter(l => l.document_id === selectedDocument.id).map(link => {
                                const name = link.entity_type === "scenario"
                                  ? scenarios.find(s => s.id === link.entity_id)?.name
                                  : thresholds.find(t => t.id === link.entity_id)?.name;
                                return (
                                  <span key={link.id} className="text-xs bg-slate-100 text-slate-700 px-2 py-0.5 rounded">
                                    {link.entity_type}: {name || link.entity_id?.slice(0, 6)}
                                  </span>
                                );
                              })
                            )}
                          </div>
                        </div>

                        {/* Link to object */}
                        <div className="border-t border-slate-100 pt-3">
                          <div className="text-xs font-medium text-slate-600 mb-2">Link to Governance Object</div>
                          <div className="space-y-2">
                            <select
                              className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm bg-white"
                              value={linkForm.entityType}
                              onChange={e => setLinkForm(f => ({ ...f, entityType: e.target.value, entityId: "" }))}
                            >
                              <option value="scenario">Scenario</option>
                              <option value="threshold">Threshold</option>
                            </select>
                            <select
                              className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm bg-white"
                              value={linkForm.entityId}
                              onChange={e => setLinkForm(f => ({ ...f, entityId: e.target.value }))}
                            >
                              <option value="">Select object</option>
                              {(linkForm.entityType === "scenario" ? scenarios : thresholds).map(item => (
                                <option key={item.id} value={item.id}>{item.name}</option>
                              ))}
                            </select>
                            <select
                              className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm bg-white"
                              value={linkForm.question}
                              onChange={e => setLinkForm(f => ({ ...f, question: e.target.value }))}
                            >
                              <option value="">Linked Question (optional)</option>
                              <option value="WHO">WHO</option>
                              <option value="WHAT">WHAT</option>
                              <option value="WHEN">WHEN</option>
                              <option value="WHY">WHY</option>
                              <option value="WHERE">WHERE</option>
                              <option value="AUTHORITY">AUTHORITY</option>
                              <option value="EVIDENCE">EVIDENCE</option>
                              <option value="REVIEW">REVIEW</option>
                            </select>
                            <Button
                              variant="secondary"
                              size="sm"
                              disabled={linkingDocId === selectedDocument.id}
                              onClick={() => linkDocumentToObject(selectedDocument)}
                            >
                              {linkingDocId === selectedDocument.id ? "Linking..." : "Link Document"}
                            </Button>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex flex-col gap-2">
                          <Button variant="secondary" size="sm" onClick={() => openDocument(selectedDocument)}>
                            <Icons.Download /> Download
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => verifyDocumentIntegrity(selectedDocument)}
                            disabled={verifyingDocId === selectedDocument.id}
                          >
                            {verifyingDocId === selectedDocument.id ? "Verifying..." : "Verify Integrity"}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          </div>
    </>
  );
}

export default DocumentsTab;
